import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import notificationPreferencesService from '../../services/notificationPreferencesService';
import { supabase } from '../../lib/supabase';

import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import SMSControls from './components/SMSControls';
import EmailDigestOptions from './components/EmailDigestOptions';
import CriticalAlertSettings from './components/CriticalAlertSettings';
import DoNotDisturbScheduling from './components/DoNotDisturbScheduling';
import BudgetAlertSettings from './components/BudgetAlertSettings';
import NavigationSidebar from '../../components/NavigationSidebar';


const NotificationPreferences = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadPreferences();

      // Subscribe to real-time changes for this user's notification preferences
      const channel = supabase
        ?.channel(`notification-preferences-${user?.id}`)
        ?.on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_profiles',
            filter: `id=eq.${user?.id}`,
          },
          (payload) => {
            // Update preferences when changes occur in other sessions
            const updatedPreferences = payload?.new?.notification_preferences;
            if (updatedPreferences) {
              setPreferences(updatedPreferences);
              toast?.info('Notification preferences updated', {
                description: 'Settings synced from another session',
              });
            }
          }
        )
        ?.subscribe();

      // Cleanup subscription on unmount
      return () => {
        supabase?.removeChannel(channel);
      };
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const { data, error } = await notificationPreferencesService?.getNotificationPreferences(user?.id);
      if (error) throw error;

      const defaultPrefs = notificationPreferencesService?.getDefaultPreferences();
      setPreferences(data && Object.keys(data)?.length > 0 ? data : defaultPrefs);
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast?.error('Failed to load notification preferences');
      setPreferences(notificationPreferencesService?.getDefaultPreferences());
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await notificationPreferencesService?.updateNotificationPreferences(preferences);
      if (error) throw error;

      toast?.success('Notification preferences saved successfully', {
        description: 'Your settings have been updated',
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast?.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const updatePreferences = (section, updates) => {
    setPreferences((prev) => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        ...updates,
      },
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-muted animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <NavigationSidebar />
      <div className="flex-1 ml-0 lg:ml-64">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2 font-heading">
              Notification Preferences
            </h1>
            <p className="text-muted-foreground">
              Customize how and when you receive notifications from DraftIQ
            </p>
          </div>

          {/* Notification Settings */}
          <div className="space-y-6">
            {/* SMS Controls */}
            <SMSControls 
              preferences={preferences}
              onUpdate={updatePreferences}
            />

            {/* Email Digest Options */}
            <EmailDigestOptions 
              preferences={preferences}
              onUpdate={updatePreferences}
            />

            {/* Critical Alert Settings */}
            <CriticalAlertSettings 
              preferences={preferences}
              onUpdate={updatePreferences}
            />

            {/* Budget Alert Settings */}
            <BudgetAlertSettings 
              preferences={preferences}
              updatePreferences={updatePreferences}
            />

            {/* Do Not Disturb Scheduling */}
            <DoNotDisturbScheduling 
              preferences={preferences}
              onUpdate={updatePreferences}
            />

            {/* Save Button */}
            <div className="flex justify-end pt-6">
              <Button
                onClick={handleSave}
                disabled={saving}
                iconName={saving ? 'Loader2' : 'Save'}
                iconPosition="left"
                className={saving ? 'animate-spin' : ''}
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;